import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import placeholderImg from "../../img/accueil_concept4.png";
import { fetchEvenementPublicDetail, clearEvenementDetail } from "../../redux/agendaSlice";

const formatDateLong = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const toDateKey = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  return new Date(dateTimeStr).toLocaleDateString("fr-CA"); // "YYYY-MM-DD" locale-safe
};

const formatTime = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const d = new Date(dateTimeStr);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

// Formulaire de réservation pour un événement
function FormulaireReservationEvenement({ evenement }) {
  const periodes = evenement?.periodes ?? [];

  // Dates distinctes disponibles (tri chronologique)
  const datesDisponibles = [...new Map(
    periodes
      .filter((p) => p.disponible)
      .map((p) => [toDateKey(p.date_debut), p.date_debut])
  ).entries()]
    .sort(([a], [b]) => a.localeCompare(b));

  const seuleDateDisponible = datesDisponibles.length === 1 ? datesDisponibles[0][0] : "";

  const [selectedDateKey, setSelectedDateKey] = useState(seuleDateDisponible);
  const [selectedPeriodeId, setSelectedPeriodeId] = useState("");

  // Créneaux du jour sélectionné
  const creneauxDuJour = periodes.filter(
    (p) => toDateKey(p.date_debut) === selectedDateKey && p.disponible
  );

  const seulCreneauId =
    creneauxDuJour.length === 1 ? creneauxDuJour[0].periode_evenement_id : "";

  const periodeSélectionnée = periodes.find(
    (p) => p.periode_evenement_id === selectedPeriodeId
  ) ?? null;

  const capaciteMax = periodeSélectionnée?.capacite_max ?? null;
  const placesRestantes = periodeSélectionnée?.places_restantes ?? null;

  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [nombreParticipants, setNombreParticipants] = useState(1);
  const [commentaire, setCommentaire] = useState("");
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Auto-sélectionne le créneau si un seul disponible, réinitialise sinon
  useEffect(() => {
    setSelectedPeriodeId(seulCreneauId);
  }, [seulCreneauId]);

  const validate = () => {
    const e = {};
    if (!selectedDateKey) e.date = "Veuillez choisir une date";
    if (!selectedPeriodeId) e.creneau = "Veuillez choisir un créneau";
    if (!nom.trim()) e.nom = "Nom requis";
    if (!prenom.trim()) e.prenom = "Prénom requis";
    if (!email.trim()) e.email = "Email requis";
    else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = "Email invalide";
    if (!telephone.trim()) e.telephone = "Téléphone requis";
    else if (!/^\+?\d{6,15}$/.test(telephone.replace(/\s/g, "")))
      e.telephone = "Téléphone invalide (6 à 15 chiffres)";
    if (nombreParticipants < 1) e.nombreParticipants = "Au moins 1 participant";
    if (!cgvAccepted) e.cgv = "Veuillez accepter les CGV pour continuer";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    // TODO Étape 3.2 : connexion API réservation événement
    // selectedPeriodeId contient l'identifiant de la PeriodeEvenement choisie
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-8 text-center flex flex-col gap-4">
        <div className="text-4xl">🎉</div>
        <h3 className="text-xl font-bold text-orange-800">Demande reçue !</h3>
        <p className="text-slate-700">
          Merci <strong>{prenom} {nom}</strong>, votre demande de réservation pour{" "}
          <strong>{evenement?.nom}</strong> a bien été enregistrée.
        </p>
        <p className="text-slate-600 text-sm">
          Vous recevrez une confirmation à <strong>{email}</strong> dès que votre
          réservation sera validée par notre équipe.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-2 text-orange-600 underline text-sm hover:text-orange-800"
        >
          Faire une nouvelle demande
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-md shadow-slate-200 p-6 md:p-8">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">
        Réserver cet événement
      </h2>
      <p className="text-slate-500 text-sm mb-6">
        Remplissez le formulaire ci-dessous, nous vous confirmerons votre place
        par email.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        {/* Sélection de la date */}
        <div>
          <label className="block text-sm font-medium text-orange-600 mb-1">
            Date de l'événement
          </label>
          {datesDisponibles.length === 0 ? (
            <p className="text-slate-400 italic text-sm">Aucune date disponible.</p>
          ) : (
            <div className="relative">
              <select
                value={selectedDateKey}
                onChange={(e) => setSelectedDateKey(e.target.value)}
                disabled={datesDisponibles.length === 1}
                className={`w-full appearance-none border-b py-2 pr-8 bg-transparent focus:outline-none ${
                  datesDisponibles.length === 1
                    ? "border-slate-200 text-slate-500 cursor-default"
                    : "border-orange-300 text-slate-800 focus:border-orange-600"
                }`}
              >
                {datesDisponibles.length > 1 && <option value="">Choisir une date…</option>}
                {datesDisponibles.map(([key, dateTimeStr]) => (
                  <option key={key} value={key}>
                    {formatDateLong(dateTimeStr)}
                  </option>
                ))}
              </select>
              {datesDisponibles.length > 1 && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs">▼</span>
              )}
            </div>
          )}
          {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
        </div>

        {/* Sélection du créneau horaire */}
        <div>
          <label className="block text-sm font-medium text-orange-600 mb-1">
            Créneau horaire
          </label>
          {!selectedDateKey ? (
            <p className="text-slate-400 italic text-sm">Sélectionnez d'abord une date.</p>
          ) : creneauxDuJour.length === 0 ? (
            <p className="text-slate-400 italic text-sm">Aucun créneau disponible pour cette date.</p>
          ) : (
            <div className="relative">
              <select
                value={selectedPeriodeId}
                onChange={(e) => setSelectedPeriodeId(e.target.value)}
                disabled={creneauxDuJour.length === 1}
                className={`w-full appearance-none border-b py-2 pr-8 bg-transparent focus:outline-none ${
                  creneauxDuJour.length === 1
                    ? "border-slate-200 text-slate-500 cursor-default"
                    : "border-orange-300 text-slate-800 focus:border-orange-600"
                }`}
              >
                {creneauxDuJour.length > 1 && <option value="">Choisir un créneau…</option>}
                {creneauxDuJour.map((p) => (
                  <option key={p.periode_evenement_id} value={p.periode_evenement_id}>
                    {formatTime(p.date_debut)} → {formatTime(p.date_fin)}
                  </option>
                ))}
              </select>
              {creneauxDuJour.length > 1 && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs">▼</span>
              )}
            </div>
          )}
          {errors.creneau && <p className="text-red-500 text-xs mt-1">{errors.creneau}</p>}
        </div>

        {/* Séparateur */}
        <hr className="border-orange-100" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-orange-600 mb-1">Nom</label>
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className="w-full border-b border-orange-300 px-2 py-1 focus:outline-none focus:border-orange-600"
            />
            {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-orange-600 mb-1">Prénom</label>
            <input
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              className="w-full border-b border-orange-300 px-2 py-1 focus:outline-none focus:border-orange-600"
            />
            {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-orange-600 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border-b border-orange-300 px-2 py-1 focus:outline-none focus:border-orange-600"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-orange-600 mb-1">Téléphone</label>
          <input
            type="tel"
            value={telephone}
            onChange={(e) => setTelephone(e.target.value)}
            className="w-full border-b border-orange-300 px-2 py-1 focus:outline-none focus:border-orange-600"
            placeholder="+33 6 00 00 00 00"
          />
          {errors.telephone && <p className="text-red-500 text-xs mt-1">{errors.telephone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-orange-600 mb-1">
            Nombre de participants
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={placesRestantes ?? capaciteMax ?? 50}
              value={nombreParticipants}
              onChange={(e) => setNombreParticipants(Number(e.target.value))}
              className="w-24 border-b border-orange-300 px-2 py-1 focus:outline-none focus:border-orange-600"
            />
            {placesRestantes != null && (
              <span className="text-slate-400 text-xs">
                (max {placesRestantes} place{placesRestantes > 1 ? "s" : ""} restante{placesRestantes > 1 ? "s" : ""})
              </span>
            )}
          </div>
          {errors.nombreParticipants && (
            <p className="text-red-500 text-xs mt-1">{errors.nombreParticipants}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-orange-600 mb-1">
            Message / Commentaire{" "}
            <span className="text-slate-400 font-normal">(optionnel)</span>
          </label>
          <textarea
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            rows={3}
            className="w-full border border-orange-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm"
            placeholder="Informations complémentaires, questions…"
          />
        </div>

        <div className="pt-1">
          <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={cgvAccepted}
              onChange={(e) => setCgvAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-orange-600 border-orange-300 rounded focus:ring-orange-400"
            />
            <span>
              En envoyant ce formulaire, j'accepte les{" "}
              <a href="/cgv" className="text-orange-600 underline hover:text-orange-800">
                conditions générales de vente
              </a>
              .
            </span>
          </label>
          {errors.cgv && <p className="text-red-500 text-xs mt-1">{errors.cgv}</p>}
        </div>

        <div className="pt-2">
          <button
            type="submit"
            className="bg-orange-600 text-white px-8 py-2.5 rounded-lg font-semibold hover:bg-orange-800 transition-colors w-full sm:w-auto"
          >
            Envoyer ma demande
          </button>
        </div>
      </form>
    </div>
  );
}

// Page de détail d'un événement
function EvenementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { evenementDetail, loadingDetail, error } = useSelector((state) => state.agenda);

  useEffect(() => {
    dispatch(fetchEvenementPublicDetail(id));
    return () => {
      dispatch(clearEvenementDetail());
    };
  }, [dispatch, id]);

  if (loadingDetail) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-slate-400 italic">Chargement de l'événement…</p>
      </div>
    );
  }

  if (error || (!loadingDetail && !evenementDetail)) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-slate-700 mb-4">
          Événement introuvable
        </h2>
        <p className="text-slate-500 mb-6">
          Cet événement n'existe pas ou a été supprimé.
        </p>
        <button
          onClick={() => navigate("/agenda")}
          className="text-orange-600 font-semibold hover:text-orange-800 underline"
        >
          ← Retour à l'agenda
        </button>
      </div>
    );
  }

  const { nom, sous_titre, description, photo, periodes = [] } = evenementDetail;

  const imageUrl = photo || placeholderImg;

  const premiereDate = periodes[0]?.date_debut ? formatDateLong(periodes[0].date_debut) : null;
  const derniereDateFin =
    periodes.length > 1
      ? formatDateLong(periodes[periodes.length - 1].date_fin)
      : periodes[0]?.date_fin && periodes[0].date_fin !== periodes[0].date_debut
      ? formatDateLong(periodes[0].date_fin)
      : null;

  return (
    <div className="w-full bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12 py-10">
        <button
          onClick={() => navigate("/agenda")}
          className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-lg border border-orange-300 text-orange-600 font-semibold text-sm hover:bg-orange-50 hover:border-orange-500 hover:text-orange-800 transition-colors"
        >
          ← Retour aux événements
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Colonne gauche : formulaire */}
          <div>
            <FormulaireReservationEvenement evenement={evenementDetail} />
          </div>

          {/* Colonne droite : infos événement */}
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl overflow-hidden shadow-md max-h-72">
              <img
                src={imageUrl}
                alt={nom}
                className="w-full h-72 object-cover"
              />
            </div>

            <div className="flex flex-col gap-3">
              <h1 className="text-3xl font-bold text-slate-900">{nom}</h1>

              {sous_titre && (
                <p className="text-slate-500 text-base italic">{sous_titre}</p>
              )}


              {periodes.length > 1 && (
                <ul className="text-sm text-slate-500 space-y-1 mt-1">
                  {periodes.map((p) => (
                    <li key={p.periode_evenement_id} className="flex items-center gap-2">
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${
                          p.disponible ? "bg-green-400" : "bg-slate-300"
                        }`}
                      />
                      {formatDateLong(p.date_debut)}
                      {p.places_restantes != null && (
                        <span className="text-slate-400">
                          — {p.places_restantes} place{p.places_restantes > 1 ? "s" : ""} restante{p.places_restantes > 1 ? "s" : ""}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {periodes.length === 1 && periodes[0].places_restantes != null && (
                <p className="text-slate-500 text-sm">
                  {periodes[0].places_restantes} place{periodes[0].places_restantes > 1 ? "s" : ""} restante{periodes[0].places_restantes > 1 ? "s" : ""}
                </p>
              )}

              {description && (
                <p className="text-slate-700 leading-relaxed text-base mt-1">
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EvenementDetail;
